<div class="ov">
  <!-- ── Delivery: did the campaign get through? ── -->
  <section class="bk-panel ov__card ov__outcome" style="--i: 0" aria-labelledby="ov-outcome">
    <header class="ov__card-head">
      <h2 class="bk-eyebrow" id="ov-outcome">Delivery</h2>
      @if (outcomeState === "ready" && outcome && outcome.total > 0) {
        <span class="ov__card-note">{{ formatCount(outcome.total) }} messages sent</span>
      }
    </header>

    @if (!loaded) {
      <div class="ov__skel" aria-hidden="true">
        <span class="bk-skel" style="width: 140px; height: 40px"></span>
        <span class="bk-skel" style="width: 100%; height: 6px"></span>
        <span class="bk-skel" style="width: 60%; height: 12px"></span>
      </div>
    } @else {
      @switch (outcomeState) {
        @case ("loading") {
          <div class="ov__skel" role="status">
            <span class="bk-sr-only">Loading delivery</span>
            <span class="bk-skel" style="width: 140px; height: 40px" aria-hidden="true"></span>
            <span class="bk-skel" style="width: 100%; height: 6px" aria-hidden="true"></span>
            <span class="bk-skel" style="width: 60%; height: 12px" aria-hidden="true"></span>
          </div>
        }
        @case ("error") {
          <div class="ov__empty">
            <p class="ov__empty-body">
              Delivery receipts could not be loaded. Check your connection and try again.
            </p>
            <button type="button" class="bk-btn bk-btn--ghost" (click)="retryOutcome()">Try again</button>
          </div>
        }
        @case ("ready") {
          @if (outcome && outcome.total > 0) {
            <div class="ov__lead">
              <span class="ov__share">{{ share }}</span>
              <span class="ov__share-label">{{ shareLabel }}</span>
              @if (outcome.failed > 0) {
                <span class="ov__failed">{{ formatCount(outcome.failed) }} failed</span>
              }
            </div>

            <!-- Proportional: delivered, then failed, then no receipt yet -->
            <div class="ov__bar" role="img" [attr.aria-label]="outcomeBreakdown">
              <span class="ov__seg ov__seg--ok" [style.flex-grow]="outcome.delivered"></span>
              @if (outcome.failed > 0) {
                <span class="ov__seg ov__seg--bad" [style.flex-grow]="outcome.failed"></span>
              }
              <span class="ov__seg ov__seg--wait" [style.flex-grow]="outcome.inFlight"></span>
            </div>

            <dl class="ov__legend">
              <div>
                <dt><span class="ov__swatch" data-key="ok" aria-hidden="true"></span>Delivered</dt>
                <dd>{{ formatCount(outcome.delivered) }}</dd>
              </div>
              <div [class.is-bad]="outcome.failed > 0">
                <dt><span class="ov__swatch" data-key="bad" aria-hidden="true"></span>Failed</dt>
                <dd>{{ formatCount(outcome.failed) }}</dd>
              </div>
              <div>
                <dt><span class="ov__swatch" data-key="wait" aria-hidden="true"></span>No receipt yet</dt>
                <dd>{{ formatCount(outcome.inFlight) }}</dd>
              </div>
            </dl>
          } @else {
            <div class="ov__empty">
              <p class="ov__empty-title">No delivery receipts yet.</p>
              <p class="ov__empty-body">
                Receipts usually start arriving a few minutes after a send goes out.
              </p>
            </div>
          }
        }
        @default {
          <div class="ov__empty">
            <p class="ov__empty-title">Nothing sent yet.</p>
            <p class="ov__empty-body">
              @if (next) {
                Delivery shows here once the first send goes out, {{ countdown }}.
              } @else {
                Delivery shows here once the first send goes out. Add a schedule
                to a message to send it.
              }
            </p>
          </div>
        }
      }
    }
  </section>

  <!-- ── Up next, and the campaign in figures ── -->
  <section class="bk-panel ov__card ov__next" style="--i: 1" aria-labelledby="ov-next">
    <header class="ov__card-head">
      <h2 class="bk-eyebrow" id="ov-next">Next send</h2>
    </header>

    @if (!loaded) {
      <span class="bk-skel ov__ticket-skel" aria-hidden="true"></span>
    } @else if (next) {
      <div class="ov__ticket">
        <span class="ov__countdown">{{ countdown }}</span>
        <span class="ov__ticket-subject">{{ next.subject }}</span>
        <span class="ov__ticket-when" [attr.title]="next.exact">
          {{ next.when }}
          @if (next.zone) {
            <span class="ov__zone">{{ next.zone }}</span>
          }
        </span>
      </div>
    } @else {
      <p class="ov__quiet">
        @if (sends.length) {
          Every scheduled send has gone out.
        } @else {
          Nothing is scheduled. Add a schedule to a message on the Messages tab.
        }
      </p>
    }

    <dl class="ov__figures">
      <div>
        <dt>Recipients</dt>
        <dd>
          @if (statsReady) {
            {{ formatCount(campaignStats?.recipientsCount) }}
          } @else {
            <span class="bk-skel" style="width: 56px; height: 18px" aria-hidden="true"></span>
          }
        </dd>
      </div>
      <div>
        <dt>Messages</dt>
        <dd>
          @if (loaded) {
            {{ formatCount(messages.length) }}
          } @else {
            <span class="bk-skel" style="width: 32px; height: 18px" aria-hidden="true"></span>
          }
        </dd>
      </div>
      <div>
        <dt>Sends</dt>
        <dd>
          @if (loaded) {
            {{ formatCount(sentCount) }}<span class="ov__of"> of {{ formatCount(sends.length) }}</span>
          } @else {
            <span class="bk-skel" style="width: 48px; height: 18px" aria-hidden="true"></span>
          }
        </dd>
      </div>
    </dl>
  </section>

  <!-- ── Failed numbers, for people who can see campaign reports ── -->
  @if (outcomeState === "ready" && outcome && outcome.failed > 0 && campaign?.uuid) {
    <ng-container *hasPermission="{ category: 'CAMPAIGN_MANAGEMENT', permission: 'view-campaign-analytics' }">
      <div class="ov__wide" style="--i: 2">
        <app-campaign-failed-recipients
          [campaignUuid]="campaign?.uuid || ''"
          [campaignName]="campaign?.name || ''"
          [messageUuids]="messageUuids"
          [subjects]="subjects"
          [failedCount]="outcome.failed"
        />
      </div>
    </ng-container>
  }

  <!-- ── Every send, in order ── -->
  <section class="bk-panel ov__card ov__timeline" style="--i: 2" aria-labelledby="ov-sends">
    <header class="ov__card-head">
      <h2 class="bk-eyebrow" id="ov-sends">Sends</h2>
      @if (loaded && sends.length) {
        <span class="ov__card-note">{{ formatCount(sentCount) }} of {{ formatCount(sends.length) }} sent</span>
      }
    </header>

    @if (!loaded) {
      <div class="ov__skel-rows" aria-hidden="true">
        @for (row of [1, 2, 3]; track row) {
          <span class="bk-skel" style="height: 14px"></span>
        }
      </div>
    } @else if (sends.length) {
      <ol class="tl" id="ov-sends-list">
        @for (send of visibleSends; track send.key) {
          <li
            class="tl__row"
            [class.is-past]="send.past"
            [class.is-next]="send === next"
            [class.is-stopped]="stopped(send)"
          >
            <span class="tl__mark" aria-hidden="true"></span>
            <div class="tl__main">
              <span class="tl__when" [attr.title]="send.exact">
                {{ send.when }}
                @if (send.zone) {
                  <span class="ov__zone">{{ send.zone }}</span>
                }
              </span>
              <span class="tl__subject">{{ send.subject }}</span>
            </div>
            <div class="tl__side">
              @if (send.past) {
                @if (asSummary(sendOutcome(send)); as summary) {
                  @if (summary.total > 0) {
                    <span class="tl__primary">{{ sendShare(summary) }} delivered</span>
                    @if (summary.failed > 0) {
                      <span class="tl__failed">{{ formatCount(summary.failed) }} failed</span>
                    } @else {
                      <span class="tl__secondary">of {{ formatCount(summary.total) }}</span>
                    }
                  } @else {
                    <app-status [text]="send.status || 'Sent'" [status]="send.status || 'sent'" />
                    <span class="tl__secondary">No receipts yet</span>
                  }
                } @else if (sendOutcome(send) === "loading") {
                  <span class="bk-skel" style="width: 96px; height: 12px" aria-hidden="true"></span>
                  <span class="bk-sr-only">Loading delivery</span>
                } @else if (reportRate(send)) {
                  <span class="tl__primary">{{ reportRate(send) }}</span>
                  <span class="tl__secondary">of {{ formatCount(send.report?.totalRecipient) }}</span>
                } @else {
                  <app-status [text]="send.status || 'Sent'" [status]="send.status || 'sent'" />
                }
              } @else if (stopped(send)) {
                <app-status [text]="send.status" [status]="send.status" />
              } @else {
                <span class="tl__primary">{{ countdownOf(send) }}</span>
                @if (send.status) {
                  <app-status [text]="send.status" [status]="send.status" />
                }
              }
            </div>
          </li>
        }
      </ol>

      @if (sends.length > 8) {
        <button
          type="button"
          class="bk-more ov__toggle"
          aria-controls="ov-sends-list"
          [attr.aria-expanded]="showAllSends"
          (click)="showAllSends = !showAllSends"
        >
          @if (showAllSends) {
            Show fewer sends
          } @else {
            Show all {{ formatCount(sends.length) }} sends
          }
          <span class="bk-sym" aria-hidden="true">{{ showAllSends ? "expand_less" : "expand_more" }}</span>
        </button>
      }
    } @else {
      <div class="ov__empty">
        <p class="ov__empty-title">No sends scheduled.</p>
        <p class="ov__empty-body">Schedule a message on the Messages tab and it appears here in order.</p>
        <a class="bk-btn bk-btn--ghost" routerLink="../message-content">Go to messages</a>
      </div>
    }
  </section>

  <!-- ── Who it goes to, and what it is for ── -->
  <div class="ov__side" style="--i: 3">
    <section class="bk-panel ov__card" aria-labelledby="ov-audience">
      <header class="ov__card-head">
        <h2 class="bk-eyebrow" id="ov-audience">Audience</h2>
        <a class="bk-more" routerLink="../recipients">
          Recipients <span class="bk-sym" aria-hidden="true">arrow_outward</span>
        </a>
      </header>

      @if (!loaded) {
        <div class="ov__skel-rows ov__skel-rows--flush" aria-hidden="true">
          <span class="bk-skel" style="height: 14px; width: 70%"></span>
          <span class="bk-skel" style="height: 14px"></span>
        </div>
      } @else {
        <p class="ov__audience-lead">
          @if (statsReady) {
            <strong>{{ plural(campaignStats?.recipientsCount, "recipient", "recipients") }}</strong>{{ audienceSentence }}
          } @else {
            {{ audienceSentence }}
          }
        </p>
        <ul class="ov__groups">
          @for (group of audience; track group.label) {
            @if (group.names.length) {
              <li>
                <span class="ov__group-label">{{ group.label }}</span>
                <span class="ov__group-names">{{ groupNames(group) }}</span>
              </li>
            }
          }
        </ul>
      }
    </section>

    <section class="bk-panel ov__card" aria-labelledby="ov-goals">
      <header class="ov__card-head">
        <h2 class="bk-eyebrow" id="ov-goals">Goals</h2>
        @if (loaded) {
          <button
            type="button"
            class="bk-icon-btn"
            aria-label="Edit goals"
            matTooltip="Edit goals"
            (click)="showEditGoals = true"
          >
            <span class="bk-sym" aria-hidden="true">edit</span>
          </button>
        }
      </header>

      @if (!loaded) {
        <div class="ov__skel-rows ov__skel-rows--flush" aria-hidden="true">
          <span class="bk-skel" style="height: 14px"></span>
          <span class="bk-skel" style="height: 14px; width: 60%"></span>
        </div>
      } @else if (goals.length) {
        <ol class="ov__goals">
          @for (goal of goals; track $index) {
            <li>{{ goal.name }}</li>
          }
        </ol>
      } @else {
        <p class="ov__quiet">No goals set. Add one so everyone knows what this campaign is for.</p>
      }
    </section>
  </div>

  <!-- ── Messages ── -->
  <section class="bk-panel ov__card ov__messages" style="--i: 4" aria-labelledby="ov-messages">
    <header class="ov__card-head ov__card-head--inset">
      <h2 class="bk-eyebrow" id="ov-messages">Messages</h2>
      <a class="bk-more" routerLink="../message-content">
        All messages <span class="bk-sym" aria-hidden="true">arrow_outward</span>
      </a>
    </header>

    @if (!loaded) {
      <div class="ov__skel-rows" aria-hidden="true">
        @for (row of [1, 2]; track row) {
          <span class="bk-skel" style="height: 14px"></span>
        }
      </div>
    } @else if (messages.length) {
      <ul class="ov__msgs">
        @for (row of messages; track row.message.id) {
          <li>
            <a class="ov__msg" routerLink="../message-content">
              <span class="bk-msg ov__msg-copy">
                <span class="bk-msg__subject">{{ row.subject }}</span>
                <span class="bk-msg__preview">
                  @if (row.copy) {
                    <bk-sms-text [text]="row.copy" [inline]="true" />
                  } @else {
                    No message text
                  }
                </span>
                <span class="ov__msg-facts">
                  @if (row.from) {
                    <span>from {{ row.from }}</span>
                  }
                  @if (row.parts) {
                    <span>{{ row.parts }}</span>
                  }
                  <span>{{ row.sends }}</span>
                </span>
              </span>
              <span class="ov__msg-delivery">
                <bk-delivery
                  [uuid]="row.message.uuid"
                  [status]="row.deliveryStatus"
                  [sentAt]="row.sentAt"
                />
              </span>
            </a>
          </li>
        }
      </ul>
    } @else {
      <div class="ov__empty ov__empty--inset">
        <p class="ov__empty-title">No messages yet.</p>
        <p class="ov__empty-body">Add a message on the Messages tab, then schedule when it goes out.</p>
      </div>
    }
  </section>

  @if (showEditGoals && campaign) {
    <app-edit-goals
      [campaignGoals]="goals"
      [openModal]="showEditGoals"
      (onClose)="showEditGoals = false"
      [campaignData]="campaign"
    ></app-edit-goals>
  }
</div>
